// import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Container,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  IconButton,
  MenuItem,
  Select,
} from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  mentoringForm,
  sendWhatsAppTemplate,
  testWhatsAppConnectivity,
} from '../../api/services';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import CustomInput from '../../Components/CustomInput';
import AnimatedBorderButton from '../../Components/AnimatedBorderButton';
import CloseIcon from '@mui/icons-material/Close';

interface LeadData {
  name: string;
  email: string;
  phoneNumber: string;
  message?: string;
  type?: string;
  experience?: string;
  location?: string;
  industryExperience?: string;
  industryOther?: string;
  hyderabadZone?: string;
  whatSeek?: string;
  whatSeekOther?: string;
  status?: string;
  sessionDate: string;
  sessionTime: string;
}

interface MentoringLeadData {
  name: string;
  email: string;
  phoneNumber: string;
  platform: string;
  location: string;
  profession: string;
  experience: number;
  plan: 'beginner path' | 'growth-accelerator';
  status?: string;
  tags: string[];
  utmSource: string;
  utmCampaign: string;
  assignedTo: string;
  leadSourceFlowId: string;
  hyderabadZone?: string | string[];
  whatSeek?: string;
  // Backend expected fields
  industryExperience?: string;
  currentStatus?: string;
}

const LandingPageForm = () => {
  const [plan, setPlan] = React.useState('growth-accelerator');
  const [industryOther, setIndustryOther] = React.useState('');
  // const [profession] = React.useState('');
  const [whatSeekOther, setWhatSeekOther] = React.useState('');
  const [showThankYou, setShowThankYou] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<LeadData>({
    defaultValues: {
      industryExperience: '',
      hyderabadZone: '',
      whatSeek: '',
      status: '',
      industryOther: '',
      whatSeekOther: '',
      sessionDate: '',
      sessionTime: '',
    },
  });

  const mentoringMutation = useMutation({
    mutationFn: (mentoringData: MentoringLeadData) =>
      mentoringForm(mentoringData),
    onSuccess: () => {
      toast.success('Mentoring form submitted!');
      resetForm();
      setShowThankYou(true);
    },
    onError: (error: any) => {
      console.error('Mentoring form error:', error);
      console.error('Error response:', error?.response?.data);
      toast.error(
        error?.response?.data?.message || 'Mentoring submission failed',
      );
    },
  });

  const resetForm = () => {
    reset({
      name: '',
      email: '',
      phoneNumber: '',
      message: '',
      experience: '',
      location: '',
      industryExperience: '',
      hyderabadZone: '',
      whatSeek: '',
      status: '',
      industryOther: '',
      whatSeekOther: '',
      sessionDate: '',
      sessionTime: '',
    });
    setIndustryOther('');
    setWhatSeekOther('');
  };

  const industryExperience = watch('industryExperience');
  // const whatSeek = watch('whatSeek');

  const onSubmit = async (data: LeadData) => {
    // Validate required fields
    if (!data.name || !data.email || !data.phoneNumber) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Map form values to backend expected values
    const mapIndustryExperience = (value: string) => {
      if (value === 'realestate') return 'Real Estate';
      if (value === 'other') return 'Other';
      return 'Real Estate'; // default
    };

    const mapCurrentStatus = (value: string) => {
      const statusMap: { [key: string]: string } = {
        employed: 'Employed',
        selfemployed: 'Self-Employed',
        student: 'Student',
        transitioning: 'Transitioning',
      };
      return statusMap[value] || 'Employed';
    };

    const mapHyderabadZone = (value: string) => {
      const zoneMap: { [key: string]: string } = {
        west: 'West',
        south: 'South',
        east: 'East',
        north: 'North',
        central: 'Central',
      };
      return value ? [zoneMap[value] || 'West'] : [];
    };

    const mentoringData: MentoringLeadData = {
      name: data.name.trim(),
      email: data.email.trim(),
      phoneNumber: data.phoneNumber.trim(),
      platform: 'google',
      location: data.location || '',
      profession:
        (data.industryExperience === 'other'
          ? data.industryOther || 'Other'
          : 'Real Estate') || '',
      experience: Number(data.experience) || 0,
      plan: plan.trim() as 'beginner path' | 'growth-accelerator',
      status: data.status || 'new',
      tags: [],
      utmSource: '',
      utmCampaign: '',
      assignedTo: '',
      leadSourceFlowId: '',
      hyderabadZone: mapHyderabadZone(data.hyderabadZone || ''),
      whatSeek:
        data.whatSeek === 'other'
          ? data.whatSeekOther || ''
          : data.whatSeek || '',
      // Add backend expected fields
      industryExperience: mapIndustryExperience(
        data.industryExperience || 'realestate',
      ),
      currentStatus: mapCurrentStatus(data.status || 'employed'),
    };

    console.log('Sending mentoring data:', mentoringData);
    mentoringMutation.mutate(mentoringData);

    // Test WhatsApp connectivity first
    console.log('Testing WhatsApp connectivity...');
    const connectivityTest = await testWhatsAppConnectivity();
    console.log('Connectivity test result:', connectivityTest);

    // Send WhatsApp template
    try {
      await sendWhatsAppTemplate({
        firstName: data.name,
        sessionTitle: plan, // Track selected
        sessionDate: data.sessionDate || new Date().toLocaleDateString(),
        sessionTime: data.sessionTime || new Date().toLocaleTimeString(),
        venueOrPlace: data.location || 'Hyderabad',
        supportContact: '9885420885', // default if not provided
        phoneNumber: data.phoneNumber,
      });
      toast.success('WhatsApp confirmation sent successfully!');
    } catch (err: any) {
      console.error('Error sending WhatsApp template:', err);
      // Don't show error toast for WhatsApp failure, just log it
      // The form submission was successful, WhatsApp is optional
      console.warn(
        'WhatsApp notification failed, but form was submitted successfully',
      );
    }
  };

  return (
    <Box
      id="landing-page-form"
      sx={{
        background:
          'linear-gradient(180deg,rgba(255, 255, 255, 1) 56%, rgba(145, 194, 237, 1) 95%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 8,
      }}
    >
      <Container maxWidth="lg">
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            background: '#fff',
            border: '2px solid #0f63a5',
            borderRadius: 3,
            boxShadow: 2,
            p: { xs: 2, md: 4 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              py: 4,
            }}
          >
            {/* Track Selection */}
            <FormControl
              component="fieldset"
              sx={{
                mb: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <FormLabel component="legend">
                <Typography
                  variant="h5"
                  sx={{ color: '#0f63a5', fontWeight: 'bold' }}
                >
                  Choose The Track to Accelerate Your Real Estate Career
                </Typography>
              </FormLabel>
              <RadioGroup
                row
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                sx={{ gap: 4, mt: 2 }}
              >
                <FormControlLabel
                  value="beginner path"
                  control={<Radio color="primary" />}
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      4 HRS
                    </Typography>
                  }
                />
                <FormControlLabel
                  value="growth-accelerator"
                  control={<Radio color="primary" />}
                  label={
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      6 HRS
                    </Typography>
                  }
                />
              </RadioGroup>
            </FormControl>

            <Grid container spacing={4} sx={{ px: { xs: 1, md: 4 } }}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label=" Name"
                      type="text"
                      fullWidth
                      required
                      size="small"
                      {...field}
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      inputTextColor="#000" // ⬅️ override text
                      labelColor="#000"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label="Contact Number"
                      type="tel"
                      fullWidth
                      required
                      size="small"
                      inputTextColor="#000" // ⬅️ override text
                      labelColor="#000"
                      {...field}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      label="Email"
                      type="email"
                      fullWidth
                      required
                      size="small"
                      inputTextColor="#000" // ⬅️ override text
                      labelColor="#000"
                      {...field}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <Controller
                    name="status"
                    control={control}
                    defaultValue=""
                    rules={{ required: 'Status is required' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        displayEmpty
                        size="small"
                        sx={{
                          backgroundColor: '#fff',
                          fontWeight: 400, // 🔑 Ensures selected value is normal
                          fontSize: '14px',
                          color: field.value ? '#000' : '#888',
                          border: '1px solid #ccc',
                          borderRadius: 1,
                        }}
                        renderValue={(selected) => (
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: '14px',
                              color: selected ? '#000' : '#888',
                            }}
                          >
                            {selected || 'Select your Current Status'}
                          </Typography>
                        )}
                      >
                        <MenuItem disabled value="">
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: '14px',
                              color: '#888',
                            }}
                          >
                            Select your Current Status
                          </Typography>
                        </MenuItem>
                        <MenuItem value="employed">
                          <Typography
                            sx={{ fontWeight: 400, fontSize: '14px' }}
                          >
                            Employed
                          </Typography>
                        </MenuItem>
                        <MenuItem value="selfemployed">
                          <Typography
                            sx={{ fontWeight: 400, fontSize: '14px' }}
                          >
                            Self-employed
                          </Typography>
                        </MenuItem>
                        <MenuItem value="student">
                          <Typography
                            sx={{ fontWeight: 400, fontSize: '14px' }}
                          >
                            Student
                          </Typography>
                        </MenuItem>
                        <MenuItem value="transitioning">
                          <Typography
                            sx={{ fontWeight: 400, fontSize: '14px' }}
                          >
                            Transitioning careers
                          </Typography>
                        </MenuItem>
                      </Select>
                    )}
                  />
                  {errors.status && (
                    <Typography variant="caption" color="error">
                      {errors.status.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                {/* Industry Experience */}
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                  <FormLabel
                    component="legend"
                    sx={{ color: '#0f63a5', mb: 1, textAlign: 'start' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#0f63a5',
                        textAlign: 'start',
                        fontSize: { xs: '19px', md: 'inherit' },
                      }}
                    >
                      Industry Experience :
                    </Typography>
                  </FormLabel>

                  <Controller
                    name="industryExperience"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="realestate"
                          control={<Radio color="primary" />}
                          label={
                            <Typography
                              sx={{ fontSize: { xs: '14px', md: '16px' } }}
                            >
                              Real Estate
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="other"
                          control={<Radio color="primary" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                sx={{ fontSize: { xs: '14px', md: '16px' } }}
                              >
                                Other:
                              </Typography>
                              <TextField
                                size="small"
                                variant="standard"
                                value={industryOther}
                                onChange={(e) => {
                                  setIndustryOther(e.target.value);
                                  field.onChange('other');
                                }}
                                sx={{ ml: 1, width: 120 }}
                                disabled={field.value !== 'other'}
                              />
                            </Box>
                          }
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>

                {/* Show additional fields based on selection */}
                {(industryExperience === 'realestate' ||
                  industryExperience === 'other') && (
                  <>
                    {/* Show Zone only if realestate */}
                    {industryExperience === 'realestate' && (
                      <FormControl
                        component="fieldset"
                        sx={{ mb: 3, width: '100%' }}
                      >
                        <FormLabel
                          component="legend"
                          sx={{ color: '#0f63a5', mb: 1, textAlign: 'start' }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#0f63a5',
                              textAlign: 'start',
                              fontSize: { xs: '19px', md: 'inherit' },
                            }}
                          >
                            Hyderabad Zone (if applicable) :
                          </Typography>
                        </FormLabel>
                        <Controller
                          name="hyderabadZone"
                          control={control}
                          render={({ field }) => (
                            <RadioGroup row {...field}>
                              <FormControlLabel
                                value="west"
                                control={<Radio color="primary" />}
                                label={
                                  <Typography
                                    sx={{
                                      fontSize: { xs: '14px', md: '16px' },
                                    }}
                                  >
                                    West
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value="south"
                                control={<Radio color="primary" />}
                                label={
                                  <Typography
                                    sx={{
                                      fontSize: { xs: '14px', md: '16px' },
                                    }}
                                  >
                                    South
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value="east"
                                control={<Radio color="primary" />}
                                label={
                                  <Typography
                                    sx={{
                                      fontSize: { xs: '14px', md: '16px' },
                                    }}
                                  >
                                    East
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value="north"
                                control={<Radio color="primary" />}
                                label={
                                  <Typography
                                    sx={{
                                      fontSize: { xs: '14px', md: '16px' },
                                    }}
                                  >
                                    North
                                  </Typography>
                                }
                              />
                              <FormControlLabel
                                value="central"
                                control={<Radio color="primary" />}
                                label={
                                  <Typography
                                    sx={{
                                      fontSize: { xs: '14px', md: '16px' },
                                    }}
                                  >
                                    Central
                                  </Typography>
                                }
                              />
                            </RadioGroup>
                          )}
                        />
                      </FormControl>
                    )}

                    {/* Experience for both options */}
                    <Controller
                      name="experience"
                      control={control}
                      rules={{
                        required: 'Experience is required',
                        validate: (value) => {
                          if (!value) return 'Experience is required';
                          if (isNaN(Number(value)))
                            return 'Experience must be a number';
                          if (Number(value) < 0)
                            return 'Experience must be positive';
                          return true;
                        },
                      }}
                      render={({ field }) => (
                        <CustomInput
                          {...field}
                          label="Years of Experience"
                          type="number"
                          fullWidth
                          size="small"
                          inputTextColor="#000"
                          labelColor="#000"
                          error={!!errors.experience}
                          helperText={errors.experience?.message}
                        />
                      )}
                    />
                  </>
                )}

                {/* City only for real estate */}
                <Box sx={{ mt: 2 }}>
                  {industryExperience === 'realestate' && (
                    <Controller
                      name="location"
                      control={control}
                      render={({ field }) => (
                        <CustomInput
                          {...field}
                          label="If not Hyderabad, city:"
                          type="text"
                          fullWidth
                          required
                          size="small"
                          inputTextColor="#000"
                          labelColor="#000"
                          error={!!errors.location}
                          helperText={errors.location?.message}
                        />
                      )}
                    />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                {/* What do you seek after this training? */}
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                  <FormLabel
                    component="legend"
                    sx={{ color: '#0f63a5', mb: 1, textAlign: 'start' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#0f63a5',
                        textAlign: 'start',
                        fontSize: { xs: '19px', md: 'inherit' },
                      }}
                    >
                      What do you seek after this training?
                    </Typography>
                  </FormLabel>
                  <Controller
                    name="whatSeek"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup row {...field}>
                        <FormControlLabel
                          value="kickstart"
                          control={<Radio color="primary" />}
                          label={
                            <Typography
                              sx={{ fontSize: { xs: '14px', md: '16px' } }}
                            >
                              Kickstart Real Estate career
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="luxury"
                          control={<Radio color="primary" />}
                          label={
                            <Typography
                              sx={{ fontSize: { xs: '14px', md: '16px' } }}
                            >
                              Grow into luxury sales
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="consultant"
                          control={<Radio color="primary" />}
                          label={
                            <Typography
                              sx={{ fontSize: { xs: '14px', md: '16px' } }}
                            >
                              Become an independent consultant
                            </Typography>
                          }
                        />
                        <FormControlLabel
                          value="other"
                          control={<Radio color="primary" />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography
                                sx={{ fontSize: { xs: '14px', md: '16px' } }}
                              >
                                Other:
                              </Typography>
                              <TextField
                                size="small"
                                variant="standard"
                                value={whatSeekOther}
                                onChange={(e) => {
                                  setWhatSeekOther(e.target.value);
                                  field.onChange('other');
                                }}
                                sx={{ ml: 1, width: 180 }}
                                disabled={field.value !== 'other'}
                              />
                            </Box>
                          }
                        />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sessionDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="date"
                      fullWidth
                      label="Session Date"
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="sessionTime"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="time"
                      fullWidth
                      label="Session Time"
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  )}
                />

                {/* Submit button */}
                <Grid item xs={12} mt={1}>
                  <AnimatedBorderButton
                    type="submit"
                    borderColor="#0f63a5"
                    textColor="#0f63a5"
                  >
                    Submit
                  </AnimatedBorderButton>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Dialog
          open={showThankYou}
          onClose={() => setShowThankYou(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              border: '2px solid #0f63a5',
              backgroundColor: '#ffffff',
              textAlign: 'center',
              px: 3,
              py: 4,
              position: 'relative',
            },
          }}
        >
          <IconButton
            onClick={() => setShowThankYou(false)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: '#0f63a5',
            }}
          >
            <CloseIcon />
          </IconButton>
          <DialogTitle
            sx={{ fontWeight: 'bold', fontSize: '1.8rem', color: '#0f63a5' }}
          >
            🎉 Thank You!
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '1rem', mb: 3, color: '#0f63a5' }}>
              Your form has been submitted successfully.
            </Typography>
            <Button
              variant="contained"
              href="https://rajivwilliams.com/"
              target="_blank"
              rel="noopener"
              sx={{
                backgroundColor: '#0f63a5',
                color: '#fff',
                fontWeight: 'bold',
                textTransform: 'none',
                px: 4,
                borderRadius: 2,
                '&:hover': { backgroundColor: '#0d538f' },
              }}
            >
              Visit Website
            </Button>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default LandingPageForm;
